import { PageHeader } from '@ksyos/design-system';
import { useNavigate, useParams } from '@ksyos/router';
import { differenceWith } from 'lodash';
import { useEffect, useState } from 'react';
import { ManagerAuthorization } from '@common/enums';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { assertHasAuthorization } from '@frontend/utils';
import type { ServiceCode } from '../../../../common/enums/serviceCode';
import type { ValidationErrors } from '../../../utils/validation';
import { deviceTypeAddEditSchema } from '../../../../common/validation/deviceType';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import { AdminServicesSelector } from '../../elements/AdminServicesSelector';
import { ButtonContainer, CancelButton, FormContainer, FormHeader, Loading, SaveSubmit } from '../../elements/common';
import { CheckboxField } from '../../fields/Checkbox';
import { NullableTextField } from '../../fields/NullableText';

interface IDeviceType {
    id?: string;
    isFourteenDaysRecordingSupported: boolean;
    manufacturer: string | null;
    model: string | null;
    name: string | null;
    version: string | null;
}

interface IService {
    code: ServiceCode;
    id: string;
}

export function DeviceTypesAddEdit() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [deviceType, setDeviceType] = useState<IDeviceType>({
        name: null,
        model: null,
        version: null,
        manufacturer: null,
        isFourteenDaysRecordingSupported: false,
    });
    const [consumingServices, setConsumingServices] = useState<IService[]>([]);
    const [providingServices, setProvidingServices] = useState<IService[]>([]);
    const [previousConsumingServices, setPreviousConsumingServices] = useState<IService[]>([]);
    const [previousProvidingServices, setPreviousProvidingServices] = useState<IService[]>([]);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);

    assertHasAuthorization(ManagerAuthorization.deviceAndAccessoryTypes);

    useEffect(() => {
        if (id) {
            load();
        }
    }, [id]);

    return (
        <PageTemplate width="medium">
            <PageHeader title={id ? translateKey('deviceTypes.edit') : translateKey('deviceTypes.new')} />
            <Loading
                item={deviceType}
                loaded={(deviceTypeItem) => (
                    <FormContainer
                        formContext={{
                            placeholderGroupName: 'common',
                            item: deviceTypeItem,
                            errors: errors,
                            onChange: async (key, value) => await handleChange(key, value),
                            onDoValidate: async (key) => await validateOne(key),
                        }}
                        onSubmit={async () => await save()}
                    >
                        <FormHeader>{translateKey('deviceTypes.details')}</FormHeader>

                        <NullableTextField
                            fieldKey="name"
                            isRequired
                        />
                        <NullableTextField
                            fieldKey="model"
                            isRequired
                            placeholder={translateKey('deviceTypes.model')}
                        />
                        <NullableTextField
                            fieldKey="version"
                            isRequired
                            placeholder={translateKey('deviceTypes.version')}
                        />
                        <NullableTextField
                            fieldKey="manufacturer"
                            isRequired
                            placeholder={translateKey('deviceTypes.manufacturer')}
                        />
                        <CheckboxField
                            fieldKey="isFourteenDaysRecordingSupported"
                            label={translateKey('deviceTypes.isFourteenDaysRecordingSupported')}
                        />

                        <FormHeader>
                            {translateKey('common.selected') +
                                ' ' +
                                translateKey('practitioner.providingServices').toLocaleLowerCase()}
                        </FormHeader>
                        <AdminServicesSelector
                            onChange={(value) => setProvidingServices(value)}
                            selectedServices={providingServices}
                        />

                        <FormHeader>
                            {translateKey('common.selected') +
                                ' ' +
                                translateKey('practitioner.consumingServices').toLocaleLowerCase()}
                        </FormHeader>
                        <AdminServicesSelector
                            onChange={(value) => setConsumingServices(value)}
                            selectedServices={consumingServices}
                        />
                        <ButtonContainer
                            alignRight
                            buttons={[
                                <CancelButton
                                    disabled={isSaving}
                                    to="/deviceTypes"
                                />,
                                <SaveSubmit disabled={isSaving} />,
                            ]}
                        />
                    </FormContainer>
                )}
            />
        </PageTemplate>
    );

    async function load() {
        const result = await fetchJson(`/api/deviceTypes/${id}`, 'GET');
        const deviceTypeResult = result.json;
        setDeviceType(deviceTypeResult);
        setProvidingServices(result.json.providingServices);
        setConsumingServices(result.json.consumingServices);
        setPreviousProvidingServices(result.json.providingServices);
        setPreviousConsumingServices(result.json.consumingServices);
    }

    function handleChange<K extends keyof IDeviceType>(name: K, value: IDeviceType[K]) {
        setDeviceType({ ...deviceType, [name]: value });
    }

    function validateOne(name: keyof IDeviceType) {
        setErrors(updateValidationErrorsForOne(deviceTypeAddEditSchema, deviceType, errors, name));
    }

    async function save() {
        const isSuccessful = await saveDeviceType();
        if (isSuccessful) {
            navigate('/deviceTypes');
        }
    }

    async function saveDeviceType() {
        let url = '/api/deviceTypes';
        if (id) {
            url += `/${id}`;
        }

        const { errors: validationErrors, item } = validate(deviceTypeAddEditSchema, deviceType);
        setErrors(validationErrors);

        if (validationErrors.size > 0) {
            return false;
        }

        setIsSaving(true);
        const result = await fetchJson(url, 'POST', item);
        await saveDeviceTypeServices(result.json.id);

        return true;
    }

    async function saveDeviceTypeServices(deviceTypeId: string) {
        const addedProvidingServices = differenceWith(
            providingServices,
            previousProvidingServices,
            (lhs, rhs) => lhs.id === rhs.id
        );
        const addedConsumingServices = differenceWith(
            consumingServices,
            previousConsumingServices,
            (lhs, rhs) => lhs.id === rhs.id
        );
        const deletedProvidingServices = differenceWith(
            previousProvidingServices,
            providingServices,
            (lhs, rhs) => lhs.id === rhs.id
        );
        const deletedConsumingServices = differenceWith(
            previousConsumingServices,
            consumingServices,
            (lhs, rhs) => lhs.id === rhs.id
        );

        if (addedProvidingServices.length || addedConsumingServices.length) {
            const addServicesUrl = `/api/deviceTypes/${deviceTypeId}/services`;
            await fetchJson(addServicesUrl, 'POST', {
                providingServicesIds: addedProvidingServices.map((providingService) => providingService.id),
                consumingServicesIds: addedConsumingServices.map((consumingService) => consumingService.id),
            });
        }

        if (deletedProvidingServices.length || deletedConsumingServices.length) {
            const deleteServicesUrl = `/api/deviceTypes/${deviceTypeId}/services`;
            await fetchJson(deleteServicesUrl, 'DELETE', {
                providingServicesIds: deletedProvidingServices.map((providingService) => providingService.id),
                consumingServicesIds: deletedConsumingServices.map((consumingService) => consumingService.id),
            });
        }
    }
}
